howspace.components.colorPicker = (() => {
    'use strict';

    const DEFAULT_PALETTE = [
        '#6e47cf',
        '#ffaf96',
        '#f2eade',
        '#421c85',
        '#cec0ee',
        '#313131',
        '#fabfc5'
    ];

    const getDefaultPalette = () => {
        return DEFAULT_PALETTE;
    }

    const getPalette = (elem) => {
        // Try to find the palette from the data attribute of body element
        const palette = $('body').data('palette');
        if (palette) {
            return palette;
        } else {
            return getDefaultPalette();
        }
    }

    const getSwatches = (elem) => {
        let swatches = getPalette(elem);
        // Add white and black
        swatches.push('hsl(0, 0%, 100%)');
        swatches.push('hsl(0, 0%, 0%)');
        return swatches;
    }

    /**
     * Convert RGBA to RGB color
     * @return string
     * @param color
     * @param backgroundRGB string
     */
    const convertRGBAtoRGB = (color, backgroundRGB = '255,255,255') => {
        // This is not an rgba color, cannot convert, return original.
        if (!color.startsWith('rgba')) {
            return color;
        }

        // Define some special colors.
        if (color.replaceAll(' ', '') === 'rgba(0,0,0,0)') {
            return 'rgb(0, 0, 0)';
        }

        // Split background RGB into parts
        const backgroundRGBParts = backgroundRGB.split(',').map(Number);
        const [bgColorR, bgColorG, bgColorB] = backgroundRGBParts;

        // Split RGBA color into parts
        const parts = color
            .replace('rgba(', '')
            .replace(')', '')
            .split(',')
            .map(Number);

        const [sourceR, sourceG, sourceB, sourceA] = parts;
        // Creat new rgb color with defined fomular
        // https://stackoverflow.com/questions/2049230/convert-rgba-color-to-rgb
        if (!isNaN(sourceR) && !isNaN(sourceG) && !isNaN(sourceB)) {
            const targetA = ((1 - sourceA) * bgColorR) + (sourceA * sourceR);
            const targetG = ((1 - sourceA) * bgColorG) + (sourceA * sourceG);
            const targetB = ((1 - sourceA) * bgColorB) + (sourceA * sourceB);

            return `rgb(${Math.round(targetA)}, ${Math.round(targetG)}, ${Math.round(targetB)})`;
        }

        // Return the original color if it couldn't be converted
        return color;
    }

    /**
     * @returns {Object} an object with properties r,g,b
     * @param hex
     */
    const convertHEXtoRGB = (hex) => {
        // convert shorthand form (e.g. "FFF") to full form (e.g. "FFFFFF")
        let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, function (m, r, g, b) {
            return r + r + g + g + b + b;
        });
        // convert hex string to RGB object
        let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    const convertHEXtoRGBA = (hex, alpha) => {
        const rgb = convertHEXtoRGB(hex);
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }

    /**
     * @returns {Object} an object with properties r, g, b
     * @param hsl
     */
    const convertHSLtoRGB = (hsl) => {
        // Extract HSL values from the input string
        const hslRegex = /hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/;
        const matches = hslRegex.exec(hsl);

        if (matches) {
            const h = parseInt(matches[1]);
            const s = parseInt(matches[2]) / 100;
            const l = parseInt(matches[3]) / 100;

            const k = n => (n + h / 30) % 12;
            const a = s * Math.min(l, 1 - l);
            const f = n =>
                l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

            return {
                r: Math.round(255 * f(0)),
                g: Math.round(255 * f(8)),
                b: Math.round(255 * f(4))
            };
        } else {
            // Handle invalid input
            return null;
        }
    };

    /**
     * @param color
     */
    const convertHSLAtoRGB = (color) => {
        if (!color) {
            return null;
        }
        const components = color.replace('hsla(', '').replace(')', '').split(',').map(component => component.trim());

        if (components.length === 4) {
            const [h, s, l, a] = components.map(parseFloat);

            if (!isNaN(h) && !isNaN(s) && !isNaN(l) && !isNaN(a)) {
                const hslColor = `hsl(${h}, ${s}%, ${l}%)`;
                const rgbColor = convertHSLtoRGB(hslColor);

                const rgba = `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${a})`;

                return convertRGBAtoRGB(rgba);
            }
        }

        // Handle invalid input
        return null;
    };

    /**
     * @returns {Object} an object with properties r,g,b
     * @param color
     */
    const rgb = (color) => {
        if (!color) {
            return null;
        }

        if (typeof color.r !== 'undefined' && typeof color.g !== 'undefined' && typeof color.b !== 'undefined') {
            return color;
        }

        color = convertRGBAtoRGB(color);

        let result = color.includes('rgb') ? color.replace('rgb(', '').replace(')', '').split(',') : '';
        if (result) {
            return {
                r: parseFloat(result[0]),
                g: parseFloat(result[1]),
                b: parseFloat(result[2])
            };
        }

        return getColorAsRGB(color);
    }

    /**
     * More information about the formula can be found in https://www.w3.org/TR/WCAG20/#relativeluminancedef
     * @returns {Number} the luminance of this particular color
     * @param color
     */
    const luminance = (color) => {
        if (!color) {
            return null;
        }
        // convert RGB to sRGB
        let sRGB = [color.r, color.g, color.b].map(function (value) {
            value /= 255;
            return (value <= 0.03928) ? (value / 12.92) : Math.pow(((value + 0.055) / 1.055), 2.4);
        });
        // calculate luminance
        return (sRGB[0] * 0.2126) + (sRGB[1] * 0.7152) + (sRGB[2] * 0.0722);
    }

    /**
     * More information about contrast ratio fomular can be found in https://www.w3.org/TR/WCAG20/
     * @param {String} foreground RGB or hex string for foreground color
     * @param {String} background RGB or hex string for background color
     * @returns {Number} the contrast between these two colors
     */
    const contrastRatio = (foreground, background) => {
        if (!foreground || !background) {
            return null;
        }

        let L1 = luminance(rgb(foreground));
        let L2 = luminance(rgb(background));
        return (Math.round(((Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)) * 100) / 100);
    }

    const defaultOptions = {
        // More information about the options can be found in https://github.com/SofianChouaib/alwan
        "theme": "light",
        "toggle": false, // We will use our own toggle button for controlling the color picker using howspace popover
        "popover": false, // We will render it inline
        "position": "bottom-start",
        "margin": 4,
        "preset": true,
        "color": "#000",
        "default": "#000",
        "target": "",
        "disabled": false,
        "format": "rgb",
        "singleInput": true,
        "inputs": {
            "hex": true,
            "rgb": true,
            "hsl": true
        },
        "opacity": false,
        "preview": true,
        "copy": true,
        "swatches": getSwatches(),
        "toggleSwatches": false,
        "closeOnScroll": false,
    }

    const getColorAsRGB = (color) => {
        try {
            let convertedColor = color;
            // if color is hex value, convert it to rgb
            if (color.startsWith('#')) {
                convertedColor = convertHEXtoRGB(color);
            }
            // if color is rgba value, convert it to rgb
            if (color.startsWith('rgba')) {
                convertedColor = convertRGBAtoRGB(color);
            }
            // if color is hsl value, convert it to rgb
            if (color.startsWith('hsl')) {
                convertedColor = convertHSLtoRGB(color);
            }
            // if color is hsla value, convert it to rgb
            if (color.startsWith('hsla')) {
                convertedColor = convertHSLAtoRGB(color);
            }
            return convertedColor;
        } catch (e) {
            return '';
        }
    };

    /**
     * Alwan writes a transparent colour as rgb(r, g, b, a) and hsl(h, s%, l%, a). The server and the colour
     * helpers only know that alpha as rgba() and hsla(), so the picker hands colours out in those forms.
     */
    const withAlphaFunctions = (color) => {
        if (!color || color.a >= 1) {
            return color;
        }
        return {
            ...color,
            rgb: `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`,
            hsl: `hsla(${color.h}, ${color.s}%, ${color.l}%, ${color.a})`,
        };
    };

    const alphaRgba = (s) => {
        const m = s.match(/rgba\(\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([^)]+)\s*\)/i);
        return m ? parseFloat(m[1]) : 1;
    };

    const pickerOnColorEvent = (color, contrastTarget) => {
        try {
            const convertedColor = getColorAsRGB(color);
            const colorpickerPopover = $('.alwan.alwan--open');
            const contrastContainer = colorpickerPopover.find('.contrast-ratio-container');
            const failedContrastAlert = contrastContainer.find('.alert.failed-contrast');
            const transparentContrastAlert = contrastContainer.find('.alert.transparent-contrast');
            const wcagContainer = contrastContainer.find('.wcag-container');

            // get data from input element
            const contrastTargetElement = $(contrastTarget + ':visible');
            if (contrastTargetElement.length === 0) {
                return;
            }

            // get color of contrast target
            const contrastTargetColor = contrastTargetElement.css('background-color');

            if (!contrastTargetColor) {
                contrastContainer.find('.current-contrast-ratio-container').addClass('d-none');
                contrastContainer.find('.no-current-contrast-ratio-available').removeClass('d-none');
                return;
            }
            contrastContainer.find('.current-contrast-ratio-container').removeClass('d-none');

            // Check if contrastTargetColor or convertedColor has any alpha channel
            if (alphaRgba(contrastTargetColor) < 1 || alphaRgba(color) < 1) {
                transparentContrastAlert.removeClass('d-none');
                wcagContainer.addClass('d-none');
                failedContrastAlert.addClass('d-none');
                return;
            }

            transparentContrastAlert.addClass('d-none');
            wcagContainer.removeClass('d-none');


            const ratio = contrastRatio(getColorAsRGB(contrastTargetColor), convertedColor);

            colorpickerPopover.find('.current-contrast-ratio-container .current-contrast-ratio').text(ratio);

            if (ratio >= 7.0) {
                failedContrastAlert.addClass('d-none');
                contrastContainer.addClass('passed-aa-ratio');
                contrastContainer.addClass('passed-aaa-ratio');
            } else if (ratio >= 4.5) {
                failedContrastAlert.addClass('d-none');
                contrastContainer.removeClass('passed-aaa-ratio');
                contrastContainer.addClass('passed-aa-ratio');
            } else {
                failedContrastAlert.removeClass('d-none');
                contrastContainer.removeClass('passed-aaa-ratio');
                contrastContainer.removeClass('passed-aa-ratio');
            }
        } catch (e) {
            console.error(e);
        }
    }

    const openColorPicker = (e) => {
        const toggleButton = $(e.currentTarget);
        const colorPicker = toggleButton.closest('.howspace-color-picker');

        if (colorPicker.is('.opened') || colorPicker.is('.opening') || colorPicker.is('.closing')) {
            return;
        }

        // Close all other opened color pickers except the current one
        $('.howspace-color-picker.opened').not(colorPicker).each(function () {
            closeColorPicker($(this));
        });

        colorPicker.removeClass('closed').addClass('opened');

        const alwanWrapper = colorPicker.find('.alwan-color-picker-wrapper');
        alwanWrapper.removeClass('d-none');

        howspace.popover.open(toggleButton, alwanWrapper);
    }

    const closeColorPicker = (colorPicker) => {
        colorPicker.removeClass('opened').addClass('closed');

        const alwanWrapper = colorPicker.find('.alwan-color-picker-wrapper');
        alwanWrapper.addClass('d-none');

        howspace.dispatchEvent(colorPicker, howspace.components.colorPicker.COLOR_PICKER_CLOSE);
        howspace.popover.close(colorPicker, alwanWrapper);
    }

    const handleKeyDown = e => {
        if (e.key !== 'Escape') {
            return;
        }

        const colorPickerWrapper = $(e.currentTarget).closest('.howspace-color-picker');
        if (!colorPickerWrapper.is('.opened')) {
            return;
        }

        e.stopImmediatePropagation();
        closeColorPicker(colorPickerWrapper);
    };

    const handleClickOutside = (e) => {
        if (!e.isTrusted) {
            return;
        }

        const colorPicker = $(e.target).closest('.howspace-color-picker');
        // If the click is inside a color picker, do nothing
        if (colorPicker.length > 0) {
            return;
        }

        // If the click is outside, close all color pickers
        const colorPickers = $('.howspace-color-picker.opened');
        if (colorPickers.length === 0) {
            return;
        }

        colorPickers.each(function () {
            closeColorPicker($(this));
        });
    };

    const updateToggleButton = (colorPicker, color) => {
        const colorHsl = color.hsl;
        const toggleButton = colorPicker.find('button.color-picker-toggle');
        toggleButton.css('color', colorHsl);
        toggleButton.css('background-color', colorHsl);
        toggleButton[0].style.setProperty('--color', colorHsl);

        if (colorPicker.data('label')) {
            toggleButton.attr('aria-label', colorPicker.data('label'));
        }
    }

    const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

    /**
     * Writes the colour into the hex field, unless the user is the one typing in it. Reformatting what
     * somebody is halfway through writing would fight them for the caret.
     */
    const updateHexInput = (colorPicker, color) => {
        const hexInput = colorPicker.find('.howspace-color-picker-hex');
        if (hexInput.length === 0 || hexInput.is(':focus')) {
            return;
        }

        hexInput.val(color.hex);
        howspace.form.clearInvalidField(hexInput);
    };

    const initColorPicker = async (colorPicker, options = {}) => {
        const alwanPickerElem = colorPicker.find('.alwan-color-picker')[0];
        const colorPickerOptions = $.extend({}, defaultOptions, options);

        // Set initial color if it is defined in the data attribute.
        if (colorPicker.data('color')) {
            colorPickerOptions.color = colorPicker.data('color');
            // Set also the input value
            colorPicker.find('.howspace-color-picker-input').val(colorPickerOptions.color);
        }

        if (colorPicker.data('opacity') === true) {
            colorPickerOptions.opacity = true;
        }

        await lib('alwan', 'alwan.min.css');
        await lib('alwan', 'alwan.min.js');

        const picker = new Alwan(alwanPickerElem, colorPickerOptions);

        colorPicker.addClass('howspace-color-picker-initialized');
        colorPicker.data('color-picker', picker);

        // Set the color/ background color of the button
        updateToggleButton(colorPicker, picker.getColor());
        updateHexInput(colorPicker, picker.getColor());

        // A complete hex is applied as it is typed. Anything else is flagged and left alone until blur.
        colorPicker.on('input', '.howspace-color-picker-hex', (e) => {
            const value = e.currentTarget.value.trim();
            const isHex = HEX_PATTERN.test(value);

            if (!isHex) {
                howspace.form.markInvalidField(e.currentTarget);
                return;
            }

            howspace.form.clearInvalidField(e.currentTarget);

            picker.setColor(value.startsWith('#') ? value : '#' + value);
            picker.trigger('change');
        });

        // The field must never be left showing something other than the colour the picker holds.
        colorPicker.on('blur', '.howspace-color-picker-hex', () => {
            updateHexInput(colorPicker, picker.getColor());
        });

        colorPicker.on('click', '.color-picker-toggle', (e) => {
            const elem = $(e.currentTarget).closest('.howspace-color-picker');
            if (elem.hasClass('closed')) {
                openColorPicker(e);
            } else {
                closeColorPicker(elem);
            }
        });

        colorPicker.on('keydown', handleKeyDown);

        colorPicker.on(howspace.popover.POPOVER_OPENED, () => {
            // Remove the contrast ratio template if it exists
            const alwanWrapper = colorPicker.find('.alwan-color-picker-wrapper');
            if (alwanWrapper.is(':hidden')) {
                return;
            }

            const alwanColorPicker = alwanWrapper.find('.alwan.alwan--open');
            const selectedColor = howspace.components.colorPicker.getColor(colorPicker);

            alwanColorPicker.find('.contrast-ratio-container').remove();
            howspace.dispatchEvent(colorPicker, howspace.components.colorPicker.COLOR_PICKER_OPEN, {
                color: selectedColor
            });

            if (colorPicker.data('contrast-target')) {
                const contrastRatioTemplate = $('.color-contrast-ratio-template');
                if (contrastRatioTemplate.length === 0) {
                    return;
                }
                let contrastTemplate = contrastRatioTemplate.html().trim();
                alwanColorPicker.find('.alwan__swatches').before(contrastTemplate);
                pickerOnColorEvent(selectedColor.rgb, colorPicker.data('contrast-target'));
            }
        });

        picker.on('change', (alwanColor) => {
            const color = withAlphaFunctions(alwanColor);
            howspace.dispatchEvent(colorPicker, howspace.components.colorPicker.COLOR_PICKER_CHANGE, {
                color: color
            });

            updateToggleButton(colorPicker, color);
            updateHexInput(colorPicker, color);

            // Set the color of the input field the value can be in the format of rgb, hex, hsl
            colorPicker.find('.howspace-color-picker-input').val(color.hsl);
            colorPicker.dataAndAttr('color', color.hsl);
        });

        picker.on('color', (alwanColor) => {
            const color = withAlphaFunctions(alwanColor);
            updateToggleButton(colorPicker, color);
            pickerOnColorEvent(color.rgb, colorPicker.data('contrast-target'));
            howspace.dispatchEvent(colorPicker, howspace.components.colorPicker.COLOR_PICKER_PICK, {
                color: color
            });
        });

        colorPicker.addClass('color-picker-initialized');
        colorPicker.trigger(howspace.components.colorPicker.COLOR_PICKER_INITIALIZED);
    };

    return {
        COLOR_PICKER_INITIALIZED: 'color-picker-initialized',
        COLOR_PICKER_OPEN: 'color-picker-open',
        COLOR_PICKER_CLOSE: 'color-picker-close',
        COLOR_PICKER_CHANGE: 'color-picker-change',
        COLOR_PICKER_PICK: 'color-picker-pick',
        init: function (elem, options) {
            if (!(elem instanceof jQuery)) {
                elem = $(elem);
            }
            if (!options) {
                options = {};
            }
            elem.findIncludingSelf('.howspace-color-picker:not(.howspace-color-picker-initialized)').each(function () {
                initColorPicker($(this), options);
            });

            document.body.addEventListener('click', handleClickOutside);
        },
        setColor: async function (elem, color) {
            if (!(elem instanceof jQuery)) {
                elem = $(elem);
            }

            if (!elem.hasClass('color-picker-initialized')) {
                // Wait for color picker to be initialized.
                elem.one(howspace.components.colorPicker.COLOR_PICKER_INITIALIZED, () => {
                    howspace.components.colorPicker.setColor(elem, color);
                });
                return;
            }

            if (!elem.data('color-picker') || !color) {
                return;
            }

            const colorPicker = elem.data('color-picker');
            colorPicker.setColor(color);
            colorPicker.trigger('change');
            colorPicker.trigger('color');
        },
        getColor: function (elem) {
            if (!(elem instanceof jQuery)) {
                elem = $(elem);
            }
            if (!elem.data('color-picker')) {
                return;
            }
            const colorPicker = elem.data('color-picker');
            return withAlphaFunctions(colorPicker.getColor());
        },
        getPalette: getPalette,
        getDefaultPalette: getDefaultPalette,
        convertHEXtoRGBA: convertHEXtoRGBA,
        contrastRatio: contrastRatio,
        resetColor: function (elem) {
            if (!(elem instanceof jQuery)) {
                elem = $(elem);
            }
            if (!elem.data('color-picker')) {
                return;
            }
            const colorPicker = elem.data('color-picker');
            colorPicker.reset();
        },
        trigger: function (elem, event) {
            if (!(elem instanceof jQuery)) {
                elem = $(elem);
            }
            if (!elem.hasClass('howspace-color-picker-initialized')) {
                // Wait until the color picker is initialized, then trigger the event.
                elem.one(howspace.components.colorPicker.COLOR_PICKER_INITIALIZED, () => {
                    howspace.components.colorPicker.trigger(elem, event);
                });
                return;
            }
            if (!elem.data('color-picker')) {
                return;
            }
            const colorPicker = elem.data('color-picker');
            colorPicker.trigger(event);
        },
        destroy: function (elem) {
            elem.findIncludingSelf('.howspace-color-picker-initialized').each(function () {
                const colorPicker = $(this).data('color-picker');
                colorPicker?.destroy();
                $(this).removeClass('color-picker-initialized');
            });
        },
    };

})();
